import { useId } from "react";
import { Navigate, useParams } from "react-router";
import { exportStaffEmployees, type QueueSection, type QueueSort, type SectionKey, type StaffEmployeeRow, type StaffView } from "@/lib/staff-api";
import { csvFilename, employeesCsv } from "@/lib/staff-export";
import { apiParams, readDir } from "@/lib/list-url";
import { formatDate } from "@/lib/formatters";
import { STATUS_LABELS } from "@/lib/roster";
import { staffCan } from "@/lib/staff-capabilities";
import { useStaffQueue } from "@/lib/staff-hooks";
import { STAFF_TABS } from "@/lib/staff-tabs";
import { useStaffAuth } from "@/lib/use-staff-auth";
import { CompanyLink } from "./CompanyLink";
import { DownloadCsvButton } from "./DownloadCsvButton";
import { EmployeeLink } from "./EmployeeLink";
import { EmployeesTable } from "./EmployeesTable";
import { Loaded } from "./Loaded";
import { StageFilter, UrlTickbox } from "./ListControls";
import { Pager } from "./Pager";
import { QueueRow, ReplacementQueueRow } from "./QueueRow";
import { useListUrl, usePageCorrection, useSortHandler } from "./use-list-url";

const QUEUE_SORT_OPTIONS: ReadonlyArray<[QueueSort, string]> = [
  ["waiting", "Longest waiting"],
  ["company", "Company (A–Z)"],
  ["name", "Last name (A–Z)"],
  ["stage", "Stage"],
];

/** What an empty section says, where an empty section shows (Today leaves them out). */
const EMPTY_TEXT: Partial<Record<SectionKey, string>> = {
  verify: "No names to verify.",
  waiting_on_you: "Nothing here is waiting on you.",
  waiting_on_others: "Nobody is waiting on an employee or BIM.",
  make: "No badges to make.",
  delivered: "No badges waiting to be delivered.",
};

/** A queue section's own parameters: page_<key>, sort_<key> and dir_<key>. */
const QUEUE_PARAM = /^(page|sort|dir)_/;

/** The queue tabs send only their sections' own parameters. A hand-typed
 *  includeHidden or includeInactive never reaches a queue: hidden companies and
 *  offboarded employees are never work. */
function useQueueParams(): URLSearchParams {
  const [url] = useListUrl();
  const out = new URLSearchParams();
  for (const [key, value] of url) if (QUEUE_PARAM.test(key)) out.append(key, value);
  return out;
}

interface QueueSectionViewProps {
  section: QueueSection;
  /** False while the query shows placeholder data, which is the previous
   *  page: correcting the URL from it would undo a pager click. */
  settled: boolean;
  variant?: "process";
}

/** One server section of cards, with its own Sort by menu and pager. */
function QueueSectionView({ section, settled, variant }: QueueSectionViewProps): React.ReactElement {
  const headingId = useId();
  const sortId = useId();
  const [url, change] = useListUrl();
  const pageParam = `page_${section.key}`;
  const sortParam = `sort_${section.key}`;
  usePageCorrection(pageParam, settled ? section.page : undefined);
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={headingId} className="text-lg font-semibold text-gray-900">
          {section.title} <span className="font-normal text-gray-500">({section.total})</span>
        </h2>
        {section.total > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <label htmlFor={sortId} className="font-medium text-gray-800">
              Sort by
            </label>
            <select
              id={sortId}
              // The URL, not the served section: while the new order loads, the
              // placeholder still holds the old sort and the menu would snap back.
              value={url.get(sortParam) ?? section.sort}
              onChange={(e) => change({ [sortParam]: e.target.value, [pageParam]: null })}
              className="rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"
            >
              {QUEUE_SORT_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {section.total === 0 ? (
        <p className="text-sm text-gray-600">{EMPTY_TEXT[section.key] ?? ""}</p>
      ) : (
        <ul className="space-y-3">
          {section.rows.map((r) =>
            r.kind === "employee" ? (
              <QueueRow key={r.row.id} employee={r.row} variant={variant} />
            ) : (
              <ReplacementQueueRow key={`${r.row.order.id}:${r.row.employeeId}`} row={r.row} />
            ),
          )}
        </ul>
      )}
      <Pager
        page={section.page}
        pageSize={section.pageSize}
        total={section.total}
        onPage={(p) => change({ [pageParam]: p === 1 ? null : String(p) })}
        label={section.title}
      />
    </section>
  );
}

function RecentActivity({ rows }: { rows: StaffEmployeeRow[] }): React.ReactElement {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h2 id={headingId} className="text-lg font-semibold text-gray-900">
        Recent activity
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-600">No recent activity.</p>
      ) : (
        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white text-sm">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap justify-between gap-2 px-4 py-2">
              <span>
                <EmployeeLink employee={r} />
                {" · "}
                <CompanyLink vendorId={r.vendorId} vendor={r.vendor} />
                {" — "}
                {r.inactive ? "No longer employed" : STATUS_LABELS[r.status]}
              </span>
              <span className="text-gray-500">{formatDate(r.updatedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Everything waiting on you, grouped by the work it is, then what changed. */
function TodayTab(): React.ReactElement {
  const query = useStaffQueue("today", useQueueParams());
  return (
    <Loaded queries={[query]}>
      {() => {
        const { sections, meta } = query.data!;
        const shown = sections.filter((s) => s.total > 0);
        return (
          <div className="space-y-8">
            {shown.length === 0 && <p className="text-sm text-gray-600">Nothing is waiting on you.</p>}
            {shown.map((s) => (
              <QueueSectionView key={s.key} section={s} settled={!query.isPlaceholderData} />
            ))}
            <RecentActivity rows={meta.recentActivity ?? []} />
          </div>
        );
      }}
    </Loaded>
  );
}

/** Verify, Background process and Badges to make: every section, empty ones included. */
function QueueTab({ view, variant }: { view: Exclude<StaffView, "today" | "all">; variant?: "process" }): React.ReactElement {
  const query = useStaffQueue(view, useQueueParams());
  return (
    <Loaded queries={[query]}>
      {() => (
        <div className="space-y-8">
          {query.data!.sections.map((s) => (
            <QueueSectionView key={s.key} section={s} settled={!query.isPlaceholderData} variant={variant} />
          ))}
        </div>
      )}
    </Loaded>
  );
}

/** Every employee, as a sortable table. */
function EmployeesTab(): React.ReactElement {
  const { staff } = useStaffAuth();
  const canSeeHidden = staff !== null && staffCan(staff.role, "manage_staff");
  const [url, change] = useListUrl();
  const params = apiParams(url, canSeeHidden);
  const query = useStaffQueue("all", params);
  const section = query.data?.sections[0];
  // Placeholder data is the previous page: never correct the URL from it.
  usePageCorrection("page", query.isPlaceholderData ? undefined : section?.page);
  const onSort = useSortHandler();
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <UrlTickbox label="Show employees no longer employed" param="includeInactive" />
        {canSeeHidden && <UrlTickbox label="Show hidden companies" param="includeHidden" />}
        <StageFilter />
        <DownloadCsvButton build={async () => employeesCsv(await exportStaffEmployees(params))} filename={() => csvFilename("employees")} />
      </div>
      <Loaded queries={[query]}>
        {() =>
          !section || section.total === 0 ? (
            <p className="text-sm text-gray-600">No employees to show.</p>
          ) : (
            <div className="space-y-3">
              <EmployeesTable
                caption="Employees"
                rows={section.rows.flatMap((r) => (r.kind === "employee" ? [r.row] : []))}
                sort={url.get("sort") ?? "name"}
                dir={readDir(url)}
                onSort={onSort}
                canAct
              />
              <Pager
                page={section.page}
                pageSize={section.pageSize}
                total={section.total}
                onPage={(p) => change({ page: p === 1 ? null : String(p) })}
                label="Employees"
              />
            </div>
          )
        }
      </Loaded>
    </div>
  );
}

const TAB_BODIES: Record<StaffView, () => React.ReactElement> = {
  today: TodayTab,
  verify: () => <QueueTab view="verify" />,
  process: () => <QueueTab view="process" variant="process" />,
  badges: () => <QueueTab view="badges" />,
  all: EmployeesTab,
};

/** One dashboard tab, chosen by the path: /staff is Today, /staff/<view> the rest. */
export function StaffDashboardPage(): React.ReactElement {
  const { view } = useParams();
  const path = view === undefined ? "/staff" : `/staff/${view}`;
  const tab = STAFF_TABS.find((t) => t.path === path);

  if (!tab) {
    return <Navigate to="/staff" replace />;
  }

  const Body = TAB_BODIES[tab.view];
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{tab.label}</h1>
      <Body />
    </div>
  );
}
